const MS_IN_DAY = 24 * 60 * 60 * 1000;

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const parseNotificationData = (rawData) => {
  if (!rawData) return {};
  if (typeof rawData === 'object') return rawData;
  if (typeof rawData === 'string') {
    try {
      return JSON.parse(rawData);
    } catch (error) {
      return {};
    }
  }
  return {};
};

export const getNotificationMessage = (notification) => {
  if (typeof notification?.message === 'string' && notification.message.trim()) {
    return notification.message.trim();
  }

  const data = parseNotificationData(notification?.data);
  const fallback = data.message || data.body || data.text;
  return typeof fallback === 'string' && fallback.trim()
    ? fallback.trim()
    : 'No details available.';
};

export const getNotificationTitle = (notification) => {
  const data = parseNotificationData(notification?.data);
  const candidates = [
    notification?.title,
    data.title,
    data.subject,
    notification?.type,
  ];

  const value = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim());
  return value ? value.trim() : 'Notification';
};

const normalizeToken = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const normalizeRole = (role) => normalizeToken(role || 'employee');

const isHrAdminRole = (role) => ['hr_admin', 'admin_hr', 'hr', 'hr_coordinator'].includes(normalizeRole(role));

const isVacationReviewerRole = (role) =>
  [
    'manager',
    'hod',
    'head_of_department',
    'head_department',
    'operation_manager',
    'operations_manager',
    'operations',
    'ceo',
    'finance',
    'executive',
  ].includes(normalizeRole(role));

const normalizeWorkflowScope = (value, fallback = 'my_requests') => {
  const scope = normalizeToken(value);
  if (['assigned_requests', 'assigned', 'pending', 'team_requests', 'team'].includes(scope)) {
    return 'assigned_requests';
  }
  if (['archive_requests', 'archive', 'archived', 'history'].includes(scope)) {
    return 'archive_requests';
  }
  if (['pre_approval_revisions', 'revisions', 'revision_requests'].includes(scope)) {
    return 'pre_approval_revisions';
  }
  if (['my_requests', 'my', 'mine', 'personal'].includes(scope)) {
    return 'my_requests';
  }
  return fallback;
};

const getVacationRouteByRole = (role, scope) => {
  const normalizedScope = normalizeWorkflowScope(scope, '');

  if (normalizedScope === 'my_requests') {
    return '/employee/vacation-requests';
  }

  if (isHrAdminRole(role)) {
    return '/admin/vacation-requests';
  }

  if (isVacationReviewerRole(role)) {
    return '/manager/vacation-requests';
  }

  return '/employee/vacation-requests';
};

const getDashboardRouteByRole = (role) => {
  const normalizedRole = normalizeRole(role);
  switch (normalizedRole) {
    case 'hr_admin':
    case 'admin_hr':
      return '/admin/dashboard';
    case 'manager':
    case 'hod':
    case 'head_of_department':
    case 'head_department':
    case 'operation_manager':
    case 'operations_manager':
    case 'operations':
      return '/manager/dashboard';
    case 'ceo':
      return '/ceo/dashboard';
    case 'finance':
    case 'executive':
      return '/manager/dashboard';
    case 'employee':
    case 'finance_coordinator':
      return '/employee/dashboard';
    default:
      return '/notifications';
  }
};

const normalizeAction = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `/${trimmed}`;
};

const getNotificationExplicitAction = (notification) => {
  const data = parseNotificationData(notification?.data);
  return normalizeAction(
    data.action_url || data.actionUrl || data.url || data.route || data.path || null
  );
};

const getNotificationText = (notification) =>
  `${getNotificationTitle(notification)} ${getNotificationMessage(notification)}`;

const getNumericId = (value) => {
  if (value == null) return null;
  const match = String(value).trim().match(/^\d+$/);
  return match ? match[0] : null;
};

const getRequestIdFromText = (notification) => {
  const text = getNotificationText(notification);
  const patterns = [
    /\brequest\b[^\d]{0,30}\bid\s*[:#]?\s*(\d+)/i,
    /\brequest\s*#\s*(\d+)/i,
    /\b(?:request_id|record_id|entity_id|id)\s*[:#]\s*(\d+)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1];
  }

  return null;
};

const getNotificationRequestId = (notification) => {
  const data = parseNotificationData(notification?.data);
  const candidates = [
    data.request_id,
    data.hr_request_id,
    data.vacation_request_id,
    data.record_id,
    data.entity_id,
    data.id,
    data.request?.id,
  ];

  for (const candidate of candidates) {
    const id = getNumericId(candidate);
    if (id) return id;
  }

  return getRequestIdFromText(notification);
};

const getNotificationRequestType = (notification) => {
  const data = parseNotificationData(notification?.data);
  const explicitType = normalizeToken(
    data.request_type ||
      data.requestType ||
      data.hr_request_type ||
      data.request?.request_type ||
      ''
  );

  if (explicitType) return explicitType;

  const text = getNotificationText(notification);
  const quotedRequestType = text.match(/['"]([a-z][a-z0-9_\s-]{2,})['"]\s+request/i);
  return quotedRequestType?.[1] ? normalizeToken(quotedRequestType[1]) : '';
};

const getDefaultHrScope = (notification, role) => {
  const text = getNotificationText(notification).toLowerCase();
  if (text.includes('your request')) return 'my_requests';
  if (
    text.includes('assigned') ||
    text.includes('waiting for') ||
    text.includes('needs your') ||
    text.includes('pending your') ||
    text.includes('review')
  ) {
    return 'assigned_requests';
  }
  return isVacationReviewerRole(role) || isHrAdminRole(role) ? 'assigned_requests' : 'my_requests';
};

const buildHrRequestDetailAction = (notification, role) => {
  const requestId = getNotificationRequestId(notification);
  if (!requestId) return null;

  const data = parseNotificationData(notification?.data);
  const scope = normalizeWorkflowScope(
    data.notification_scope || data.scope || data.view || data.view_mode,
    getDefaultHrScope(notification, role)
  );
  const requestType = getNotificationRequestType(notification);
  const params = new URLSearchParams();
  params.set('from', scope);

  if (requestType && !['request', 'hr_request', 'all', 'vacation'].includes(requestType)) {
    params.set('request_type', requestType);
  }

  return `/hr-requests/${requestId}?${params.toString()}`;
};

const isGenericRequestAction = (action) => {
  if (!action || isExternalNotificationAction(action)) return false;
  const path = action.split('?')[0].replace(/\/+$/, '');
  return [
    '/all-requests',
    '/hr-requests',
    '/employee/vacation-requests',
    '/manager/vacation-requests',
    '/admin/vacation-requests',
  ].includes(path);
};

const isVacationNotification = (notification) => {
  const data = parseNotificationData(notification?.data);
  const explicitAction = getNotificationExplicitAction(notification) || '';
  const requestType = getNotificationRequestType(notification);
  const type = String(notification?.type || '').toLowerCase();
  const message = getNotificationMessage(notification).toLowerCase();
  const model = normalizeToken(data.model || data.workflow || data.source);

  return Boolean(
    model === 'vacation_request' ||
      requestType === 'vacation' ||
      explicitAction.includes('vacation-requests') ||
      type.includes('vacation') ||
      message.includes('vacation')
  );
};

const isHrRequestNotification = (notification) => {
  const data = parseNotificationData(notification?.data);
  const explicitAction = getNotificationExplicitAction(notification) || '';
  const requestType = getNotificationRequestType(notification);
  const type = String(notification?.type || '').toLowerCase();
  const message = getNotificationMessage(notification).toLowerCase();
  const text = `${type} ${message}`;
  const model = normalizeToken(data.model || data.workflow || '');

  if (model === 'hr_request') return true;
  if (requestType && requestType !== 'vacation') return true;
  if (explicitAction.includes('/hr-requests') || explicitAction.includes('/all-requests')) return true;

  return [
    'hr request',
    'pre-approval',
    'pre approval',
    'missing punch',
    'finance claim',
    'business trip',
    'resignation',
    'loan',
    'workflow task',
  ].some((keyword) => text.includes(keyword));
};

const isAnnouncementNotification = (notification) => {
  const data = parseNotificationData(notification?.data);
  const explicitAction = getNotificationExplicitAction(notification) || '';
  const type = String(notification?.type || '').toLowerCase();
  const message = getNotificationMessage(notification).toLowerCase();

  return Boolean(
    data.announcement_id ||
    explicitAction.includes('section=announcements') ||
    type.includes('announcement') ||
    message.includes('announcement')
  );
};

const isHolidayNotification = (notification) => {
  const data = parseNotificationData(notification?.data);
  const explicitAction = getNotificationExplicitAction(notification) || '';
  const type = String(notification?.type || '').toLowerCase();
  const message = getNotificationMessage(notification).toLowerCase();

  return Boolean(
    data.holiday_id ||
    explicitAction.includes('section=holidays') ||
    type.includes('holiday') ||
    message.includes('holiday')
  );
};

/**
 * Coarse category for a notification — drives which icon the inbox shows.
 * Reuses the existing type classifiers first, then keyword fallbacks.
 */
export const getNotificationCategory = (notification) => {
  const data = parseNotificationData(notification?.data);
  const blob = `${String(notification?.type || '')} ${getNotificationMessage(notification)} ${normalizeToken(
    data.model || data.workflow || data.source || ''
  )}`.toLowerCase();

  if (isVacationNotification(notification)) return 'vacation';
  if (isAnnouncementNotification(notification)) return 'announcement';
  if (isHolidayNotification(notification)) return 'holiday';
  if (blob.includes('appraisal') || blob.includes('performance')) return 'appraisal';
  if (blob.includes('asset')) return 'asset';
  if (blob.includes('balance')) return 'balance';
  if (
    blob.includes('exit') ||
    blob.includes('re-entry') ||
    blob.includes('reentry') ||
    blob.includes('re_entry') ||
    blob.includes('iqama') ||
    blob.includes('visa')
  ) {
    return 'exit_entry';
  }
  if (isHrRequestNotification(notification)) return 'request';
  return 'default';
};

/**
 * Status flavor for a notification — drives the icon color
 * (success / error / warning / info).
 */
export const getNotificationTone = (notification) => {
  const blob = `${String(notification?.type || '')} ${getNotificationMessage(notification)}`.toLowerCase();

  if (/(reject|declin|denied|refus)/.test(blob)) return 'error';
  if (/(approv|accepted|completed|granted|confirmed|success)/.test(blob)) return 'success';
  if (/(pending|await|waiting|submitted|requires|needs|review|cancel|on hold|on-hold)/.test(blob)) {
    return 'warning';
  }
  return 'info';
};

export const resolveNotificationAction = (notification, role = 'employee') => {
  const normalizedAction = getNotificationExplicitAction(notification);
  const hrRequestDetailAction = isHrRequestNotification(notification)
    ? buildHrRequestDetailAction(notification, role)
    : null;

  if (normalizedAction && (!isGenericRequestAction(normalizedAction) || !hrRequestDetailAction)) {
    return normalizedAction;
  }

  const type = String(notification?.type || '').toLowerCase();
  const message = getNotificationMessage(notification).toLowerCase();

  if (hrRequestDetailAction) {
    return hrRequestDetailAction;
  }

  if (isVacationNotification(notification)) {
    const data = parseNotificationData(notification?.data);
    return getVacationRouteByRole(role, data.notification_scope || data.scope);
  }

  if (isHrRequestNotification(notification)) {
    return '/all-requests';
  }

  if (type.includes('appraisal') || message.includes('appraisal')) {
    return isHrAdminRole(role) ? '/performance/appraisals' : '/performance/appraisals/my-tasks';
  }

  if (type.includes('announcement') || message.includes('announcement')) {
    return '/notifications?section=announcements';
  }

  if (type.includes('holiday') || message.includes('holiday')) {
    return '/notifications?section=holidays';
  }

  return getDashboardRouteByRole(role);
};

export const isExternalNotificationAction = (action) =>
  typeof action === 'string' && /^https?:\/\//i.test(action.trim());

export const getNotificationActionLabel = (notification, role = 'employee') => {
  const action = resolveNotificationAction(notification, role);
  const normalizedAction = String(action || '').toLowerCase();
  const type = String(notification?.type || '').toLowerCase();
  const message = getNotificationMessage(notification).toLowerCase();

  if (isExternalNotificationAction(action)) return 'Open link';
  if (normalizedAction.includes('vacation') || type.includes('vacation') || message.includes('vacation')) {
    return 'View request';
  }
  if (
    normalizedAction.includes('all-requests') ||
    normalizedAction.includes('hr-requests') ||
    type.includes('hr request') ||
    message.includes('hr request')
  ) {
    return 'View request';
  }
  if (normalizedAction.includes('appraisal') || type.includes('appraisal') || message.includes('appraisal')) {
    return 'View appraisal';
  }
  if (normalizedAction.includes('announcements') || type.includes('announcement') || message.includes('announcement')) {
    return 'View announcements';
  }
  if (normalizedAction.includes('holidays') || type.includes('holiday') || message.includes('holiday')) {
    return 'View holidays';
  }
  if (normalizedAction.includes('dashboard')) return 'Open dashboard';
  if (normalizedAction.includes('notifications')) return 'Open notification center';

  return 'Open';
};

export const getNotificationActionHint = (notification, role = 'employee') => {
  const action = resolveNotificationAction(notification, role);
  if (!action) return '';

  if (isExternalNotificationAction(action)) {
    try {
      return new URL(action).hostname;
    } catch {
      return action;
    }
  }

  return action;
};

export const parseDateValue = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]) - 1;
      const day = Number(dateOnlyMatch[3]);
      return new Date(year, month, day);
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value) => {
  const date = parseDateValue(value);
  if (!date) return '';

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDateOnly = (value) => {
  const date = parseDateValue(value);
  if (!date) return '';

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const isAnnouncementActive = (announcement, now = new Date()) => {
  if (!announcement || !announcement.is_active) return false;

  const start = parseDateValue(announcement.start_date);
  const end = parseDateValue(announcement.end_date);

  const startsOk = !start || start <= now;
  const endsOk = !end || end >= now;
  return startsOk && endsOk;
};

export const isHolidayUpcoming = (holiday, now = new Date()) => {
  if (!holiday || !holiday.is_active) return false;

  const holidayDate = parseDateValue(holiday.holiday_date);
  if (!holidayDate) return false;

  return getStartOfDay(holidayDate) >= getStartOfDay(now);
};

export const daysUntilDate = (value, now = new Date()) => {
  const target = parseDateValue(value);
  if (!target) return null;

  const diff = getStartOfDay(target).getTime() - getStartOfDay(now).getTime();
  return Math.ceil(diff / MS_IN_DAY);
};

export const isNavbarNotificationVisible = (
  notification,
  { announcements = [], holidays = [], now = new Date() } = {}
) => {
  if (!notification || notification.read) return false;

  const data = parseNotificationData(notification.data);

  if (isAnnouncementNotification(notification)) {
    const announcementId = data.announcement_id;
    if (announcementId == null) return false;

    const announcement = announcements.find(
      (item) => String(item?.id) === String(announcementId)
    );

    return Boolean(announcement && isAnnouncementActive(announcement, now));
  }

  if (isHolidayNotification(notification)) {
    const holidayId = data.holiday_id;
    if (holidayId == null) return false;

    const holiday = holidays.find(
      (item) => String(item?.id) === String(holidayId)
    );

    return Boolean(holiday && isHolidayUpcoming(holiday, now));
  }

  return true;
};
